import { Link } from 'react-router-dom'
import { ShieldCheckIcon } from '@heroicons/react/24/solid'
import EngineRecordView from '../components/trackRecord/EngineRecordView'
import PaperRecordView from '../components/trackRecord/PaperRecordView'
import Phase2DeciderNote from '../components/trackRecord/Phase2DeciderNote'
import { RecordToggle, recordLabel, useRecordSource } from '../components/trackRecord/recordSource'
import LiveRecordSection from '../components/trading/LiveRecordSection'
import { useAuthStore } from '../store/authStore'
import { FEE_CALCULATOR_PATH } from '../routes'

const BANNER_DETAIL = {
  paper:
    'Pretend $10,000 on real prices, traded by a frozen rule set. Each trade is recorded by the daily run when it closes, net of the 0.25% fee per side, and is never mixed with the signal engine’s record.',
  engine:
    'A signal is recorded when the engine closes it at its stop, its target, or expiry. Demo and paper test signals are excluded.',
} as const

// Dated history line for the engine record: the card's step-1 advice was
// removed on 2026-10-09 after the exit backtest (admin Exit Backtest page).
const STEP1_REMOVAL_NOTE =
  'Until Oct 9, 2026, signal cards also suggested taking half off at step 1 and moving the stop to entry. The record never scored that suggestion; a backtest on the 147 closed signals found it did not improve results, so it was removed. How the record scores signals is unchanged.'

/**
 * The automated track record, switchable between the signal engine and the
 * live Paper forward test (?record=...; the default is DEFAULT_RECORD_SOURCE in
 * components/trackRecord/recordSource.tsx, due to flip to Paper before
 * launch). Both views count
 * every close, show losses at full weight and keep the win rate beside its
 * denominator. Nothing here is self-reported: the personal trade journal lives
 * on My Profile and never counts.
 */
const TrackRecord = () => {
  const [source, setSource] = useRecordSource()
  const isAdmin = useAuthStore((state) => state.user?.is_superuser)

  return (
    <div className="space-y-6 text-gray-100">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-50">Track Record</h1>
          <p className="mt-1 text-sm text-gray-400">
            Every trade closed, recorded automatically. Nothing here is self-reported.
          </p>
        </div>
        {/* The fee calculator opens from here, not the sidebar. */}
        <div className="flex gap-4">
          <Link to={FEE_CALCULATOR_PATH} className="text-sm font-semibold text-primary-400 hover:text-primary-300">
            Fee calculator →
          </Link>
          <Link to="/signals" className="text-sm font-semibold text-primary-400 hover:text-primary-300">
            Open signals →
          </Link>
        </div>
      </div>

      <RecordToggle source={source} onChange={setSource} />

      <div
        role="note"
        className="flex items-start gap-3 rounded-lg border border-green-500/30 bg-green-500/10 px-4 py-3"
        data-testid="transparency-banner"
      >
        <ShieldCheckIcon aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-green-400" />
        <div>
          <p className="text-sm font-semibold text-green-100">
            Every closed signal is listed — wins and losses. Any exclusion is shown with its reason; nothing is deleted.
          </p>
          <p className="mt-0.5 text-xs text-gray-400">
            <span className="font-medium text-gray-300">
              {recordLabel(source)}.
            </span>{' '}
            {BANNER_DETAIL[source]}
          </p>
          {source === 'engine' && (
            <p className="mt-1 text-xs text-gray-400" data-testid="step1-removal-note">
              {STEP1_REMOVAL_NOTE}
            </p>
          )}
          {source === 'engine' && <Phase2DeciderNote />}
        </div>
      </div>

      {/* Keyed so each view mounts fresh: no numbers carry across the switch. */}
      {source === 'engine' ? <EngineRecordView key="engine" /> : <PaperRecordView key="paper" />}

      {/* Real-money Kraken trades at actual fills: admins only, never mixed into the records above. */}
      {isAdmin && <LiveRecordSection />}
    </div>
  )
}

export default TrackRecord
